import { DeleteFilled, PushpinFilled, PushpinTwoTone } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Col, Dropdown, Form, InputNumber, Menu, Modal, Row, Space } from "antd";
import { ColumnsType } from "antd/lib/table";
import Paragraph from "antd/lib/typography/Paragraph";
import SortableTable from "components/SortableTable";
import { useAuthenticatedRequest, useAuthenticatedRequestCreator } from "hooks/useRequests";
import { Rule, RulePin } from "models/rules";
import { useState } from "react";
import NewRuleModal from "./Rules/NewRuleModal";
import TestRulesModal from "./Rules/TestRulesModal";
import SettingsHeader from "./SettingsHeader";

const useCardTitleRules = () => {
    const queryClient = useQueryClient();
    const ruleQueryKey = ['website-settings', 'card-title-rules'];
    const mutationOptions = {
        onSuccess: () => queryClient.invalidateQueries(ruleQueryKey)
    };

    const fetchRules = useAuthenticatedRequest<Rule[]>({
        method: 'get',
        url: '/website-settings/card-title-rules'
    });

    const addRule = useAuthenticatedRequestCreator<void, Rule>(
        (rule) => ({
            method: 'post',
            url: '/website-settings/card-title-rules/add',
            data: rule
        })
    );

    const reorderRules = useAuthenticatedRequestCreator<void, Rule[]>(
        (rules) => ({
            method: 'post',
            url: '/website-settings/card-title-rules/re-order',
            data: rules.map((r, idx) => ({ id: r.id, ordinal: idx + 1 }))
        })
    );

    const removeRule = useAuthenticatedRequestCreator<void, string>(
        (ruleId) => ({
            method: 'post',
            url: `/website-settings/card-title-rules/${ruleId}/remove`
        })
    );

    const updateRulePin = useAuthenticatedRequestCreator<void, Rule>(
        (rule) => ({
            method: 'post',
            url: '/website-settings/card-title-rules/update-pin',
            data: { id: rule.id, pin: rule.pin }
        })
    );

    const { data, isLoading } = useQuery(ruleQueryKey, fetchRules);

    const { mutateAsync: saveRule, isLoading: isCreating } = useMutation(addRule, mutationOptions);

    const { mutateAsync: changeRuleOrder, isLoading: isSaving } = useMutation(reorderRules, mutationOptions);

    const { mutateAsync: deleteRule, isLoading: isDeleting } = useMutation(removeRule, mutationOptions);

    const { mutateAsync: updateRule, isLoading: isUpdating } = useMutation(updateRulePin, mutationOptions);

    return {
        rules: data,
        isWorking: isLoading || isCreating || isSaving || isDeleting || isUpdating,
        saveRule,
        changeRuleOrder,
        deleteRule,
        updateRule
    };
}

const RuleOrdinalModal = ({ rule, onOrdinalChange, maxOrd }: {
    rule: Rule,
    onOrdinalChange: (rule: Rule, oldOrd: number, newOrd: number) => Promise<void>,
    maxOrd: number
}) => {
    const [open, setOpen] = useState(false);
    const [newOrd, setNewOrd] = useState<number | null>(rule.ordinal);

    const submit = async () => {
        await onOrdinalChange({ ...rule, ordinal: newOrd! }, rule.ordinal, newOrd!);
        setOpen(false);
    }

    const cancel = () => {
        setNewOrd(rule.ordinal);
        setOpen(false);
    }

    return <>
        <Button type="link" onClick={() => setOpen(true)}>#{rule.ordinal}</Button>
        <Modal
            title="Change Rule Order"
            open={open}
            onOk={async () => await submit()}
            onCancel={cancel}
            closable={false}
            keyboard={false}
            maskClosable={false}
            destroyOnClose
            okButtonProps={{ disabled: newOrd === null || newOrd <= 0 }}
        >
            <Space direction="vertical" size={25}>
                <Space>{rule.pin === RulePin.None ? null : <PushpinTwoTone />}{rule.description}</Space>
                <Form.Item label="New Ordinal" help="Rule ordering will be constrained by pin state">
                    <InputNumber
                        prefix="#"
                        min={1}
                        max={maxOrd}
                        value={newOrd}
                        onChange={setNewOrd}
                        style={{ width: '100px' }}
                    />
                </Form.Item>
            </Space>
        </Modal>
    </>
}

const CardTitlePreQASection = () => {
    const {
        rules = [],
        isWorking,
        saveRule,
        changeRuleOrder,
        deleteRule,
        updateRule
    } = useCardTitleRules();

    const changeRulePin = async (rule: Rule, pin: RulePin) => {
        await updateRule({ ...rule, pin });
    }

    const columns: ColumnsType<Rule> = [
        {
            key: 'actions',
            render: (_, rule) => <Space>
                <Button danger
                    icon={<DeleteFilled />}
                    onClick={async () => await deleteRule(rule.id)}
                />
                <Dropdown
                    overlay={() => <Menu items={[
                        {
                            key: "top",
                            label: "Top",
                            disabled: rule.pin === RulePin.Top,
                            onClick: async () => changeRulePin(rule, RulePin.Top)
                        },
                        {
                            key: "none",
                            label: "None",
                            disabled: rule.pin === RulePin.None,
                            onClick: async () => changeRulePin(rule, RulePin.None)
                        },
                        {
                            key: "bottom",
                            label: "Bottom",
                            disabled: rule.pin === RulePin.Bottom,
                            onClick: async () => changeRulePin(rule, RulePin.Bottom)
                        }
                    ]} />}>
                    <Button>
                        <Space>
                            <PushpinFilled />
                        </Space>
                    </Button>
                </Dropdown>
            </Space>,
            width: 100,
        },
        {
            title: 'Order',
            dataIndex: 'ordinal',
            width: 100,
            align: 'center',
            render: (_, rule) => <RuleOrdinalModal
                key={`${rule.id}-${rule.ordinal}`}
                maxOrd={rules.length}
                rule={rule}
                onOrdinalChange={async (_, oldOrd, newOrd) => {
                    const update = [...rules];
                    const rule = update.splice(oldOrd - 1, 1)[0];
                    update.splice(newOrd - 1, 0, rule);
                    await changeRuleOrder(update);
                }}
            />
        },
        {
            title: 'Rule',
            dataIndex: 'description',
            render: (desc, r) => r.pin === RulePin.None
                ? desc
                : <Space direction="horizontal">
                    <PushpinTwoTone />
                    {desc}
                </Space>
        }
    ]

    return <Row gutter={[32, 16]} style={{ marginBottom: '32px' }}>
        <Col span={24}>
            <SettingsHeader title="Card Title Processing" />
            <Paragraph>
                Update the current batch number, and/or define pre-QA processing rules for card titles. These rules will be executed <br />
                in-order as a pipeline (output of one becomes input of next) when card titles are imported from a 3rd party vendor.
            </Paragraph>
        </Col>
        <Col span={16}>
            <Space direction="vertical">
                <Space>
                    <NewRuleModal
                        onRuleCreated={saveRule}
                    />
                    <TestRulesModal />
                </Space>
                <SortableTable
                    columns={columns}
                    data={rules}
                    isLoading={isWorking}
                    onReorder={async (reorderedRules) => await changeRuleOrder(reorderedRules)}
                    scroll={{ y: 300 }}
                />
            </Space>
        </Col>
    </Row>
};

export default CardTitlePreQASection;